/**
 * VerificationDOSPublic - Portail public de vérification des attestations DOS
 * COPPAM – Garanties Solidaires
 * 
 * Accessible sans authentification via QR code
 * Affiche : Statut, Type projet, Territoire, Millésime fonds
 * Aucune donnée financière exposée
 * 
 * Charte graphique ESS :
 * - Vert ESS principal : #0A7F5A
 * - Bleu institutionnel : #0B3C5D
 * - Sable clair : #F4F6F3
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, CheckCircle, XCircle, Clock, MapPin, Building2, Calendar, AlertTriangle, Search, QrCode } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Charte graphique ESS COPPAM
const ESS_COLORS = {
  vertPrincipal: '#0A7F5A',
  bleuInstitutionnel: '#0B3C5D',
  sableClair: '#F4F6F3',
  grisStructurant: '#4B5563',
  statutActif: '#16A34A',
  statutSuspendu: '#F59E0B',
  statutExpire: '#DC2626'
};

// Composant de statut
const StatusBadge = ({ status }) => {
  const config = {
    'active': { label: 'ACTIF', color: ESS_COLORS.statutActif, icon: CheckCircle },
    'actif': { label: 'ACTIF', color: ESS_COLORS.statutActif, icon: CheckCircle },
    'expiré': { label: 'EXPIRÉ', color: ESS_COLORS.statutExpire, icon: XCircle },
    'expired': { label: 'EXPIRÉ', color: ESS_COLORS.statutExpire, icon: XCircle },
    'en_attente': { label: 'EN ATTENTE', color: ESS_COLORS.statutSuspendu, icon: Clock },
    'pending': { label: 'EN ATTENTE', color: ESS_COLORS.statutSuspendu, icon: Clock },
  };
  
  const { label, color, icon: Icon } = config[status?.toLowerCase()] || config['en_attente'];
  
  return (
    <Badge 
      className="text-white text-lg px-4 py-2 flex items-center gap-2"
      style={{ backgroundColor: color }}
    >
      <Icon className="h-5 w-5" />
      {label}
    </Badge>
  );
};

export default function VerificationDOSPublic() {
  const { attestationId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  
  // Récupérer l'ID depuis l'URL ou les params
  const idFromUrl = attestationId || searchParams.get('id') || '';
  
  // Vérifier l'attestation
  const verifyAttestation = async (id) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dos/verify/${id}`);
      
      if (response.data.success) {
        setResult(response.data.verification);
      } else {
        setError('Attestation non trouvée');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Attestation non trouvée. Vérifiez le numéro saisi.');
      } else {
        setError('Erreur de vérification. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Vérifier automatiquement si ID présent dans l'URL
  useEffect(() => {
    if (idFromUrl) {
      setSearchInput(idFromUrl);
      verifyAttestation(idFromUrl);
    }
  }, [idFromUrl]);
  
  // Soumettre la recherche
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      verifyAttestation(searchInput.trim());
    }
  };
  
  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${ESS_COLORS.bleuInstitutionnel} 0%, ${ESS_COLORS.vertPrincipal} 100%)` }}>
      {/* Header COPPAM */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-white" />
            <div>
              <div className="text-xs text-white/70">COPPAM – Garanties Solidaires</div>
              <h1 className="text-xl font-bold text-white">DOS Solidaire</h1>
              <p className="text-xs text-white/60">Vérification Publique</p>
            </div>
          </div>
          <Badge className="text-white border-white/30" style={{ backgroundColor: `${ESS_COLORS.vertPrincipal}40` }}>
            Outre-mer
          </Badge>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Titre */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20 mb-6">
            <QrCode className="h-10 w-10 text-emerald-400" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">
            Vérification d&apos;Attestation
          </h2>
          <p className="text-emerald-200/80 max-w-lg mx-auto">
            Vérifiez l&apos;authenticité d&apos;une attestation Dommage-Ouvrage Solidaire 
            en saisissant son numéro de référence.
          </p>
        </div>
        
        {/* Formulaire de recherche */}
        <Card className="bg-white/5 border-white/10 backdrop-blur-sm mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Ex: DOS-20260123-ABC12345"
                  className="pl-12 h-14 bg-white/10 border-white/20 text-white placeholder:text-gray-400 text-lg"
                  data-testid="input-attestation-id"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading || !searchInput.trim()}
                className="h-14 px-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="btn-verify"
              >
                {loading ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  'Vérifier'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
        
        {/* Résultat de la vérification */}
        {loading && (
          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-12 text-center">
              <div className="animate-pulse">
                <Shield className="h-16 w-16 text-emerald-400 mx-auto mb-4 animate-bounce" />
                <p className="text-white text-lg">Vérification en cours...</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {error && !loading && (
          <Card className="bg-red-500/10 border-red-500/30 backdrop-blur-sm">
            <CardContent className="p-8 text-center">
              <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-300 mb-2">
                Vérification échouée
              </h3>
              <p className="text-red-200/80">{error}</p>
            </CardContent>
          </Card>
        )}
        
        {result && !loading && (
          <Card className="bg-white/5 border-white/10 backdrop-blur-sm overflow-hidden" data-testid="verification-result">
            {/* Status header */}
            <div className={`p-6 ${result.statut === 'active' || result.statut === 'actif' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/70 mb-1">Numéro d&apos;attestation</p>
                  <p className="text-2xl font-mono font-bold text-white">
                    DOS-{result.attestation_id}
                  </p>
                </div>
                <StatusBadge status={result.statut} />
              </div>
            </div>
            
            <CardContent className="p-6 space-y-6">
              {/* Informations principales */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-300 mb-2">
                    <Building2 className="h-5 w-5" />
                    <span className="text-sm font-medium">Type de projet</span>
                  </div>
                  <p className="text-xl font-semibold text-white capitalize">
                    {result.type_projet || 'N/A'}
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-300 mb-2">
                    <MapPin className="h-5 w-5" />
                    <span className="text-sm font-medium">Territoire</span>
                  </div>
                  <p className="text-xl font-semibold text-white">
                    {result.territoire || 'N/A'}
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-300 mb-2">
                    <Calendar className="h-5 w-5" />
                    <span className="text-sm font-medium">Date d&apos;émission</span>
                  </div>
                  <p className="text-xl font-semibold text-white">
                    {result.date_emission ? new Date(result.date_emission).toLocaleDateString('fr-FR') : 'N/A'}
                  </p>
                </div>
                
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-300 mb-2">
                    <Clock className="h-5 w-5" />
                    <span className="text-sm font-medium">Date d&apos;expiration</span>
                  </div>
                  <p className="text-xl font-semibold text-white">
                    {result.date_expiration ? new Date(result.date_expiration).toLocaleDateString('fr-FR') : 'N/A'}
                  </p>
                </div>
              </div>
              
              {/* Millésime du fonds */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-300">Millésime du fonds de garantie</p>
                    <p className="text-2xl font-bold text-white">{result.millesime_fonds || '2026'}</p>
                  </div>
                  <Shield className="h-12 w-12 text-emerald-400/50" />
                </div>
              </div>
              
              {/* Vérification authentique */}
              {result.verified && (
                <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                  <div>
                    <p className="font-semibold text-green-300">Document vérifié</p>
                    <p className="text-sm text-green-200/70">
                      Cette attestation est authentique et provient du système DOS Solidaire.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Mention juridique */}
        <div className="mt-10 p-6 rounded-xl" style={{ backgroundColor: `${ESS_COLORS.statutSuspendu}20`, border: `1px solid ${ESS_COLORS.statutSuspendu}50` }}>
          <div className="flex gap-3">
            <AlertTriangle className="h-6 w-6 flex-shrink-0" style={{ color: ESS_COLORS.statutSuspendu }} />
            <div className="text-sm text-white/90">
              <strong style={{ color: ESS_COLORS.statutSuspendu }}>Mention juridique :</strong> Le dispositif DOS constitue 
              un mécanisme solidaire de mutualisation des risques de nature civile, fondé sur 
              l&apos;adhésion volontaire, la prévention et un fonds de garantie mutualisé. 
              <strong> Il ne constitue ni une assurance, ni une garantie automatique au sens du Code des assurances.</strong>
            </div>
          </div>
        </div>
        
        {/* Footer COPPAM */}
        <footer className="mt-12 text-center">
          <p className="text-white/80 text-sm font-medium">
            COPPAM – Garanties Solidaires
          </p>
          <p className="text-white/50 text-sm mt-1">
            DOS Solidaire • Dispositif ESS Outre-mer
          </p>
          <p className="text-white/40 text-xs mt-2">
            Déploiement initial : Guadeloupe • Mayotte • La Réunion
          </p>
          <p className="text-white/30 text-xs mt-1">
            Pré-paramétrés : Martinique • Guyane • Saint-Martin • Saint-Barthélemy
          </p>
        </footer>
      </main>
    </div>
  );
}
